import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { KioskPanel } from "./KioskPanel";
import { Search, User, LogOut, ChevronDown, Menu, Package, Building2, MapPin, Monitor, ShoppingBag, Bell, X, AlertTriangle, BarChart3, CheckCheck, WifiOff } from "lucide-react";

const ROLE_LABELS = { super_duper_admin: "Super-Duper Admin", super_admin: "Super Admin", finance: "Finance", kiosk_manager: "Kiosk Manager" };
const TYPE_ICON = { product: Package, building: Building2, location: MapPin, kiosk: Monitor, transaction: ShoppingBag };
const NTYPE_ICON = {
  purchase: { icon: ShoppingBag, cls: "bg-emerald-100 text-emerald-600" },
  daily_summary: { icon: BarChart3, cls: "bg-indigo-100 text-indigo-600" },
  low_stock: { icon: AlertTriangle, cls: "bg-amber-100 text-amber-600" },
  offline: { icon: WifiOff, cls: "bg-rose-100 text-rose-600" },
};

function Avatar({ user, size = "h-8 w-8" }) {
  if (user?.avatar_url) return <img src={user.avatar_url} alt="" className={`${size} rounded-full object-cover`} />;
  const initials = (user?.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  return <div className={`${size} rounded-full bg-gradient-to-br from-indigo-600 to-fuchsia-600 text-white flex items-center justify-center text-xs font-bold`}>{initials}</div>;
}

export function AdminTopBar({ onHamburger }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [openResults, setOpenResults] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [counts, setCounts] = useState({ incidents: 0, notifications: 0 });
  const [kioskPanelId, setKioskPanelId] = useState(null);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [bellOpen, setBellOpen] = useState(false);
  const [notifs, setNotifs] = useState({ incidents: [], items: [] });

  const openBell = () => {
    const next = !bellOpen;
    setBellOpen(next);
    if (next) {
      if (counts.offline_in_app !== false) {
        api.get("/incidents?status=open&acknowledged=false").then((r) => setNotifs((n) => ({ ...n, incidents: r.data || [] }))).catch(() => {});
      } else {
        setNotifs((n) => ({ ...n, incidents: [] }));
      }
      api.get("/notifications?limit=20").then((r) => setNotifs((n) => ({ ...n, items: r.data || [] }))).catch(() => {});
    }
  };

  const clickNotif = (item) => {
    setBellOpen(false);
    if (!item.read && item.id) {
      api.post(`/notifications/${item.id}/read`).catch(() => {});
      setCounts((c) => ({ ...c, notifications: Math.max(0, (c.notifications || 0) - 1) }));
    }
    setNotifs((n) => ({ ...n, items: n.items.filter((x) => x.id !== item.id) }));
    if (item.link) navigate(item.link);
  };

  const markAllRead = (e) => {
    e.stopPropagation();
    api.post("/notifications/read-all").catch(() => {});
    setNotifs((n) => ({ ...n, items: [] }));
    setCounts((c) => ({ ...c, notifications: 0 }));
  };

  const dismissIncident = (i) => {
    setBellOpen(false);
    if (i.id) api.post(`/incidents/${i.id}/acknowledge`).catch(() => {});
    setNotifs((n) => ({ ...n, incidents: n.incidents.filter((x) => (x.id || x.kiosk_id) !== (i.id || i.kiosk_id)) }));
    setCounts((c) => ({ ...c, incidents: Math.max(0, (c.incidents || 0) - 1) }));
    if (i.kiosk_id) setKioskPanelId(i.kiosk_id);
  };

  useEffect(() => { api.get("/controller/counts").then((r) => setCounts(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    if (!q.trim()) { setResults([]); setOpenResults(false); return; }
    const t = setTimeout(() => {
      api.get(`/search?q=${encodeURIComponent(q.trim())}`).then((r) => { setResults(r.data); setOpenResults(true); }).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const go = (r) => {
    setQ(""); setResults([]); setOpenResults(false); setMobileSearchOpen(false);
    if (r.type === "kiosk") { setKioskPanelId(r.id); return; }
    navigate(r.to);
  };

  const searchResults = openResults ? (
    <>
      <div className="fixed inset-0 z-30" onClick={() => setOpenResults(false)} />
      <div data-testid="admin-search-results" className="absolute left-0 z-40 mt-2 w-full rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 shadow-xl max-h-96 overflow-y-auto">
        {results.length ? results.map((r) => {
          const Icon = TYPE_ICON[r.type] || Search;
          return (
            <button key={r.type + r.id} data-testid={`search-result-${r.id}`} onClick={() => go(r)} className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 dark:hover:bg-slate-900 text-left">
              <Icon className="h-4 w-4 text-slate-400 shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{r.label}</p>
                <p className="text-xs text-slate-400 truncate">{r.sublabel}</p>
              </div>
              <span className="ml-auto text-[10px] uppercase tracking-wide text-slate-300 dark:text-slate-600">{r.type}</span>
            </button>
          );
        }) : <p className="px-4 py-4 text-sm text-slate-400">No matches.</p>}
      </div>
    </>
  ) : null;

  return (
    <header className="h-16 flex items-center gap-3 px-4 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 shrink-0 z-20">
      <button data-testid="admin-hamburger" onClick={onHamburger} className="md:hidden h-9 w-9 rounded-lg flex items-center justify-center text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800">
        <Menu className="h-5 w-5" />
      </button>

      <div className="hidden md:block relative flex-1 max-w-xl">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <input
          data-testid="admin-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => results.length && setOpenResults(true)}
          placeholder="Search everywhere — products, sites, kiosks…"
          className="w-full h-10 pl-9 pr-3 rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 text-sm outline-none focus:border-blue-500"
        />
        {searchResults}
      </div>

      <button data-testid="admin-search-mobile-btn" onClick={() => setMobileSearchOpen(true)} className="md:hidden ml-auto h-9 w-9 rounded-full flex items-center justify-center text-slate-500 hover:bg-neutral-100">
        <Search className="h-5 w-5" />
      </button>

      {mobileSearchOpen && (
        <div className="md:hidden absolute inset-0 z-50 bg-white dark:bg-slate-950 flex items-center gap-2 px-4">
          <Search className="h-4 w-4 text-slate-400 shrink-0" />
          <input
            autoFocus
            data-testid="admin-search-input-mobile"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onFocus={() => results.length && setOpenResults(true)}
            placeholder="Search everywhere…"
            className="flex-1 h-10 text-base outline-none bg-transparent text-slate-900 dark:text-white"
          />
          <button data-testid="admin-search-close" onClick={() => { setMobileSearchOpen(false); setQ(""); setOpenResults(false); }} className="h-9 w-9 rounded-full flex items-center justify-center text-slate-500 hover:bg-neutral-100"><X className="h-5 w-5" /></button>
          {searchResults}
        </div>
      )}

      <div className="ml-auto relative">
        <button data-testid="topbar-bell" title="Notifications" onClick={openBell} className="relative h-9 w-9 rounded-full flex items-center justify-center text-slate-500 hover:bg-neutral-100 transition-colors">
          <Bell className="h-5 w-5" />
          {(counts.incidents + counts.notifications) > 0 && <span data-testid="topbar-bell-badge" className="absolute -top-0.5 -right-0.5 min-w-4 h-4 px-1 rounded-full text-[10px] font-bold text-white flex items-center justify-center" style={{ background: "#C026D3" }}>{counts.incidents + counts.notifications}</span>}
        </button>
        {bellOpen && (
          <>
            <div className="fixed inset-0 z-30" onClick={() => setBellOpen(false)} />
            <div data-testid="notifications-dropdown" className="absolute right-0 mt-2 w-80 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 shadow-xl z-40 overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
                <p className="font-semibold text-sm text-slate-900 dark:text-white">Notifications</p>
                {notifs.items.length > 0 && (
                  <button data-testid="notif-mark-all-read" onClick={markAllRead} className="text-xs text-indigo-600 hover:underline flex items-center gap-1"><CheckCheck className="h-3.5 w-3.5" /> Mark all read</button>
                )}
              </div>
              <div className="max-h-96 overflow-y-auto">
                {notifs.incidents.map((i) => (
                  <button key={i.id || i.kiosk_id} data-testid={`notif-incident-${i.kiosk_id}`} onClick={() => dismissIncident(i)} className="w-full text-left px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-900 flex gap-3">
                    <span className="h-8 w-8 rounded-lg bg-rose-100 text-rose-600 grid place-items-center shrink-0"><Monitor className="h-4 w-4" /></span>
                    <div className="min-w-0"><p className="text-sm font-medium text-slate-900 dark:text-white truncate">{i.kiosk_name || "Kiosk offline"}</p><p className="text-xs text-slate-400 truncate">{i.location_name ? `${i.location_name} · offline` : "Not reporting a heartbeat"}</p></div>
                  </button>
                ))}
                {notifs.items.map((it) => {
                  const meta = NTYPE_ICON[it.type] || { icon: Bell, cls: "bg-slate-100 text-slate-600" };
                  const Icon = meta.icon;
                  return (
                    <button key={it.id} data-testid={`notif-item-${it.id}`} onClick={() => clickNotif(it)} className={`w-full text-left px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-900 flex gap-3 ${it.read ? "opacity-60" : ""}`}>
                      <span className={`h-8 w-8 rounded-lg grid place-items-center shrink-0 ${meta.cls}`}><Icon className="h-4 w-4" /></span>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{it.title}</p>
                        <p className="text-xs text-slate-400 truncate">{it.body}</p>
                      </div>
                      {!it.read && <span className="h-2 w-2 rounded-full bg-indigo-500 shrink-0 mt-1.5" />}
                    </button>
                  );
                })}
                {!notifs.incidents.length && !notifs.items.length && (
                  <p data-testid="notifications-empty" className="px-4 py-8 text-center text-sm text-slate-400">You're all caught up.</p>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      <div className="relative">
        <button data-testid="admin-profile-menu-button" onClick={() => setMenuOpen((o) => !o)} className="flex items-center gap-2 rounded-full pl-2 pr-1 py-1 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
          <ChevronDown className="h-4 w-4 text-slate-400" />
          <span className="relative" data-testid="profile-menu-avatar"><Avatar user={user} /></span>
        </button>
        {menuOpen && (
          <>
            <div className="fixed inset-0 z-30" onClick={() => setMenuOpen(false)} />
            <div data-testid="profile-menu-dropdown" className="absolute right-0 mt-2 w-64 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 shadow-xl z-40 overflow-hidden">
              <div className="p-4 bg-gradient-to-br from-indigo-600 to-fuchsia-600 text-white flex items-center gap-3">
                <Avatar user={user} size="h-10 w-10" />
                <div className="min-w-0">
                  <p className="font-semibold truncate">{user?.name}</p>
                  <p className="text-xs text-white/80 truncate">{user?.email}</p>
                  <p className="text-[10px] uppercase tracking-wide text-white/70 mt-0.5">{ROLE_LABELS[user?.role]}</p>
                </div>
              </div>
              <button data-testid="profile-menu-my-profile" onClick={() => { setMenuOpen(false); navigate("/admin/profile"); }} className="w-full flex items-center gap-3 px-4 py-3 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-900">
                <User className="h-4 w-4" /> My profile
              </button>
              <div className="border-t border-slate-100 dark:border-slate-800" />
              <button data-testid="profile-menu-logout" onClick={() => { logout(); navigate("/admin/login"); }} className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/30">
                <LogOut className="h-4 w-4" /> Sign out
              </button>
            </div>
          </>
        )}
      </div>

      <KioskPanel kioskId={kioskPanelId} onClose={() => setKioskPanelId(null)} />
    </header>
  );
}
